import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Target, Eye, Compass, Users, Building, TrendingUp } from 'lucide-react';

const enablers = [
  { icon: Building, name: 'eCommerce Companies' },
  { icon: Users, name: '3PL Providers' },
  { icon: TrendingUp, name: 'Distribution Hubs' },
  { icon: Target, name: 'Manufacturing Facilities' },
  { icon: Compass, name: 'Logistics Networks' },
];

export default function About() {
  return (
    <div className="bg-[#0a1628] pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 tech-grid-bg opacity-50" />
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
                About Us
              </span>
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase leading-[0.9] tracking-tighter mb-6">
                Who We <span className="text-[#E63946]">Are</span>
              </h1>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Warehaus+ is a brand under <span className="text-white font-medium">Gravique Pvt Ltd</span> focused on transforming warehouse infrastructure through a rental-first, asset-light model.
              </p>
              <p className="text-gray-400 text-lg leading-relaxed">
                We enable businesses to scale their warehousing operations without the burden of heavy capital investment, delivering modular, future-ready infrastructure solutions.
              </p>
            </div>

            <div className="relative">
              <div className="industrial-card bg-[#0f1e32] border border-white/10 p-2">
                <img
                  src="https://images.unsplash.com/photo-1763550203439-53ae7982e6f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBpbmR1c3RyaWFsJTIwYnVpbGRpbmclMjBleHRlcmlvciUyMGFyY2hpdGVjdHVyZSUyMGRhcmt8ZW58MHx8fHwxNzcxODIyOTU1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Modern industrial building"
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#E63946] p-6">
                <div className="font-mono text-4xl font-bold text-white">2024</div>
                <div className="text-sm text-white/70 uppercase tracking-wider">Founded</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* We Enable Section */}
      <section className="py-24 md:py-32 bg-[#0c1e35]" data-testid="enablers-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
              Our Clients
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight">
              We Enable
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {enablers.map((enabler, index) => (
              <div
                key={index}
                className="industrial-card bg-[#0a1628] border border-white/10 p-6 text-center hover:border-[#E63946]/50 transition-colors group"
                data-testid={`enabler-${index}`}
              >
                <enabler.icon className="w-10 h-10 text-[#E63946] mx-auto mb-4 group-hover:text-[#f04e5b] transition-colors" />
                <p className="text-gray-300 text-sm font-medium">{enabler.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision, Mission, Goal Section */}
      <section className="py-24 md:py-32" data-testid="vmg-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Vision */}
            <div className="industrial-card bg-[#0f1e32] border border-white/10 p-8 hover:border-[#E63946]/50 transition-colors">
              <div className="w-16 h-16 bg-[#E63946]/10 flex items-center justify-center mb-6">
                <Eye className="w-8 h-8 text-[#E63946]" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white uppercase mb-4">Vision</h3>
              <p className="text-gray-400 leading-relaxed">
                To revolutionize warehouse infrastructure by making scalable, cost-effective storage accessible without upfront capital burden.
              </p>
            </div>

            {/* Mission */}
            <div className="industrial-card gold-corners bg-[#0f1e32] border border-[#E63946]/30 p-8">
              <div className="w-16 h-16 bg-[#E63946]/10 flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-[#E63946]" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white uppercase mb-4">Mission</h3>
              <p className="text-gray-400 leading-relaxed">
                Enable rapid warehouse deployment across India through flexible, rental-based fit-out and infrastructure systems.
              </p>
            </div>

            {/* Strategic Goal */}
            <div className="industrial-card bg-[#0f1e32] border border-white/10 p-8 hover:border-[#E63946]/50 transition-colors">
              <div className="w-16 h-16 bg-[#E63946]/10 flex items-center justify-center mb-6">
                <Compass className="w-8 h-8 text-[#E63946]" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white uppercase mb-4">Strategic Goal</h3>
              <p className="text-gray-400 leading-relaxed">
                Build India's largest asset-light warehousing infrastructure partner by 2030 with nationwide reach and rapid deployment capabilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="py-24 md:py-32 bg-[#0c1e35]" data-testid="founders-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
              Leadership
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight">
              Our Founders
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Founder 1 - Mohan S A */}
            <div className="industrial-card bg-[#0a1628] border border-white/10 p-8 hover:border-[#E63946]/50 transition-colors">
              <div className="w-32 h-32 rounded-full overflow-hidden mb-6 mx-auto border-2 border-[#E63946]/30">
                <img 
                  src="https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/vhxsojib_IMG_0259.jpeg"
                  alt="Mohan S A - Co-Founder"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white uppercase mb-2 text-center">Mohan S A</h3>
              <p className="text-[#E63946] text-sm uppercase tracking-wider mb-6 text-center">Co-Founder</p>
              <p className="text-gray-400 leading-relaxed text-sm">
                Mohan brings over four decades of leadership experience across business strategy, operations, and organizational transformation. Throughout his career, he has led large teams, built operational frameworks, and guided businesses through scale and structural evolution.
              </p>
              <p className="text-gray-400 leading-relaxed text-sm mt-4">
                At Warehaus+, Mohan leads operational strategy, technical & solution design for client engagement model, and governance framework.
              </p>
            </div>

            {/* Founder 2 - Anay Shukla */}
            <div className="industrial-card bg-[#0a1628] border border-white/10 p-8 hover:border-[#E63946]/50 transition-colors">
              <div className="w-32 h-32 rounded-full overflow-hidden mb-6 mx-auto border-2 border-[#E63946]/30">
                <img 
                  src="https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/snu8v9je_IMG_0257.jpeg"
                  alt="Anay Shukla - Co-Founder"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white uppercase mb-2 text-center">Anay Shukla</h3>
              <p className="text-[#E63946] text-sm uppercase tracking-wider mb-6 text-center">Co-Founder</p>
              <p className="text-gray-400 leading-relaxed text-sm">
                Anay is a supply chain leader with over two decades of experience across logistics, commercial strategy, and business execution. He has worked extensively in supply chain optimization, market development, and infrastructure-driven business models.
              </p>
              <p className="text-gray-400 leading-relaxed text-sm mt-4">
                Anay anchors the company's strategic direction, governance framework, and long-term growth roadmap, ensuring disciplined expansion aligned with market opportunity.
              </p>
            </div>

            {/* Founder 3 - Shantanu Purandare */}
            <div className="industrial-card bg-[#0a1628] border border-white/10 p-8 hover:border-[#E63946]/50 transition-colors">
              <div className="w-32 h-32 rounded-full overflow-hidden mb-6 mx-auto border-2 border-[#E63946]/30">
                <img 
                  src="https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/uob92cnm_IMG_0258.jpeg"
                  alt="Shantanu Purandare - Co-Founder"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white uppercase mb-2 text-center">Shantanu Purandare</h3>
              <p className="text-[#E63946] text-sm uppercase tracking-wider mb-6 text-center">Co-Founder</p>
              <p className="text-gray-400 leading-relaxed text-sm">
                Shantanu is a seasoned project and operations leader with extensive experience in large-scale infrastructure execution and program management. A PMP-certified professional, he brings structured delivery discipline, process rigor, and cross-functional coordination expertise.
              </p>
              <p className="text-gray-400 leading-relaxed text-sm mt-4">
                At Warehaus+, S.P. anchors the company's project delivery framework, ensuring timely deployment, operational reliability, and scalable execution standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 md:py-32" data-testid="team-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="industrial-card bg-[#0f1e32] border border-white/10 p-2">
                <img
                  src="https://images.pexels.com/photos/8961133/pexels-photo-8961133.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Warehouse team"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
                Our Team
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
                Expert Team, <span className="text-[#E63946]">Proven Results</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-6">
                Our team brings decades of combined experience in warehouse operations, logistics, and infrastructure development. We understand the challenges you face because we've solved them before.
              </p>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                From project planning to installation and ongoing support, our experts are committed to delivering excellence at every stage.
              </p>
              <Link to="/contact" data-testid="about-contact-cta">
                <Button className="btn-primary clip-slant h-12 px-8">
                  Meet Our Team <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0c1e35]">
        <div className="container-custom">
          <div className="industrial-card gold-corners bg-[#0a1628] border border-[#E63946]/30 p-12 md:p-16 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-6">
              Partner With India's Leading
              <span className="text-[#E63946]"> Warehouse Infrastructure Provider</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-10">
              Join the growing list of businesses that trust Warehaus+ for their warehouse infrastructure needs.
            </p>
            <Link to="/contact" data-testid="about-quote-cta">
              <Button className="btn-gold clip-slant h-14 px-10 text-base">
                Get Started Today <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
